function cloneListItemContent(source: HTMLLIElement, target: HTMLLIElement) {
    target.innerHTML = source.innerHTML;
    target.querySelectorAll('.ql-ui').forEach((element) => element.remove());
    target.removeAttribute('data-list');
}

function getDirectListItems(list: HTMLOListElement | HTMLUListElement): HTMLLIElement[] {
    return Array.from(list.children).filter((child): child is HTMLLIElement => child instanceof HTMLLIElement);
}

function normalizeInlineWhitespace(text: string): string {
    return text.replace(/\u00a0/g, ' ');
}

function parseRgbColorComponent(value: string): number | null {
    const parsed = Number.parseInt(value.trim(), 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 255) return null;
    return parsed;
}

function normalizeColorValue(color: string | null): string | null {
    if (!color) return null;

    const trimmed = color.trim().toLowerCase();
    const hexMatch = trimmed.match(/^#([0-9a-f]{6})$/i);
    if (hexMatch?.[1]) {
        return `#${hexMatch[1].toLowerCase()}`;
    }

    const rgbMatch = trimmed.match(/^rgb\(\s*([^,]+),\s*([^,]+),\s*([^)]+)\s*\)$/i);
    if (!rgbMatch) return null;

    const red = parseRgbColorComponent(rgbMatch[1] ?? '');
    const green = parseRgbColorComponent(rgbMatch[2] ?? '');
    const blue = parseRgbColorComponent(rgbMatch[3] ?? '');
    if (red === null || green === null || blue === null) return null;

    return `#${[red, green, blue].map((component) => component.toString(16).padStart(2, '0')).join('')}`;
}

function serializeInlineChildren(node: ParentNode): string {
    const text = Array.from(node.childNodes)
        .map((child) => serializeInlineNode(child))
        .join('');

    return normalizeInlineWhitespace(text);
}

function serializeInlineNode(node: ChildNode): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.replace(/\u00a0/g, ' ') ?? '';
    }

    if (!(node instanceof HTMLElement)) {
        return '';
    }

    const text = serializeInlineChildren(node).trim();

    if (node.tagName === 'BR') {
        return '\n';
    }

    if (!text && node.tagName !== 'IMG') {
        return '';
    }

    switch (node.tagName) {
        case 'STRONG':
        case 'B':
            return `*${text}*`;
        case 'EM':
        case 'I':
            return `_${text}_`;
        case 'U':
            return `+${text}+`;
        case 'S':
        case 'STRIKE':
        case 'DEL':
            return `-${text}-`;
        case 'A': {
            const href = node.getAttribute('href')?.trim();
            return href ? `[${text || href}|${href}]` : text;
        }
        case 'SPAN': {
            const normalizedColor = normalizeColorValue(node.style.color || node.getAttribute('color'));
            return normalizedColor ? `{color:${normalizedColor}}${text}{color}` : serializeInlineChildren(node);
        }
        case 'IMG': {
            const inlineAttachmentFileName = node.dataset.inlineAttachmentFileName?.trim();
            if (inlineAttachmentFileName) {
                return `!${inlineAttachmentFileName}!`;
            }

            const src = node.getAttribute('src')?.trim();
            return src && /^(https?:\/\/|\/)/i.test(src) ? `!${src}!` : '';
        }
        default:
            return serializeInlineChildren(node);
    }
}

function serializeListItem(item: HTMLLIElement, marker: string, depth: number): string {
    const prefix = `${marker.repeat(depth)} `;
    const segments: string[] = [];
    let inlineText = '';

    for (const child of Array.from(item.childNodes)) {
        if (child instanceof HTMLUListElement) {
            const nested = serializeList(child, depth + 1);
            if (nested) segments.push(nested);
            continue;
        }

        if (child instanceof HTMLOListElement) {
            const nested = serializeList(child, depth + 1);
            if (nested) segments.push(nested);
            continue;
        }

        inlineText += serializeInlineNode(child);
    }

    const normalizedInlineText = normalizeInlineWhitespace(inlineText).trim();
    const lines = normalizedInlineText
        ? [`${prefix}${normalizedInlineText}`, ...segments]
        : segments.length > 0
          ? segments
          : [prefix.trimEnd()];

    return lines.join('\n');
}

function serializeList(list: HTMLUListElement | HTMLOListElement, depth = 1): string {
    const marker = list.tagName === 'OL' ? '#' : '*';

    return Array.from(list.children)
        .filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
        .map((item) => serializeListItem(item, marker, depth))
        .filter((line) => line.length > 0)
        .join('\n');
}

function serializeParagraph(node: HTMLElement): string {
    const blocks: string[] = [];
    let inlineText = '';

    const flushInlineText = () => {
        const text = normalizeInlineWhitespace(inlineText).trim();
        if (text) {
            blocks.push(text);
        }
        inlineText = '';
    };

    for (const child of Array.from(node.childNodes)) {
        if (child instanceof HTMLElement && child.tagName === 'IMG') {
            flushInlineText();

            const imageMarkup = serializeInlineNode(child).trim();
            if (imageMarkup) {
                blocks.push(imageMarkup);
            }
            continue;
        }

        inlineText += serializeInlineNode(child);
    }

    flushInlineText();
    return blocks.join('\n\n');
}

function serializeBlock(node: ChildNode): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return normalizeInlineWhitespace(node.textContent ?? '').trim();
    }

    if (!(node instanceof HTMLElement)) {
        return '';
    }

    switch (node.tagName) {
        case 'P':
            return serializeParagraph(node);
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4':
        case 'H5':
        case 'H6': {
            const level = node.tagName.slice(1);
            const text = serializeInlineChildren(node).trim();
            return text ? `h${level}. ${text}` : '';
        }
        case 'UL':
        case 'OL':
            return serializeList(node as HTMLUListElement | HTMLOListElement);
        case 'DIV': {
            const nestedBlocks = Array.from(node.childNodes)
                .map((child) => serializeBlock(child))
                .filter((block) => block.length > 0);
            return nestedBlocks.join('\n\n');
        }
        default:
            return serializeInlineChildren(node).trim();
    }
}

function isEmptyParagraph(node: ChildNode): node is HTMLParagraphElement {
    return node instanceof HTMLParagraphElement && serializeInlineChildren(node).trim().length === 0;
}

/**
 * Payload emitted by the shared CommentEditor when a comment is submitted:
 * the serialized (Jira-wiki) body text plus the attachment files.
 */
export interface IncidentCommentPayload {
    text: string;
    attachments: File[];
}

// An unsent, in-progress composer snapshot (raw editor HTML + staged attachments), used to preserve
// drafts per target without serializing to Jira wiki. Shape mirrors IncidentCommentPayload but the
// text is the editor's raw HTML, not the submitted wiki markup.
export interface IncidentCommentDraft {
    text: string;
    attachments: File[];
}

export function normalizeIncidentCommentEditorHtml(html: string): string {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('ol, ul').forEach((list) => {
        const directItems = getDirectListItems(list as HTMLOListElement | HTMLUListElement);

        if (directItems.some((item) => item.dataset.list === 'bullet')) {
            const normalizedList = document.createElement('ul');
            directItems.forEach((item) => {
                const normalizedItem = document.createElement('li');
                cloneListItemContent(item, normalizedItem);
                normalizedList.appendChild(normalizedItem);
            });
            list.replaceWith(normalizedList);
            return;
        }

        if (directItems.some((item) => item.dataset.list === 'ordered')) {
            const normalizedList = document.createElement('ol');
            directItems.forEach((item) => {
                const normalizedItem = document.createElement('li');
                cloneListItemContent(item, normalizedItem);
                normalizedList.appendChild(normalizedItem);
            });
            list.replaceWith(normalizedList);
        }
    });

    tempDiv.querySelectorAll('[data-list]').forEach((element) => element.removeAttribute('data-list'));
    tempDiv.querySelectorAll('.ql-ui').forEach((element) => element.remove());

    return tempDiv.innerHTML.trim();
}

export function serializeIncidentCommentEditorToJiraWiki(html: string): string {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = normalizeIncidentCommentEditorHtml(html);
    let text = '';
    let pendingBlankParagraphs = 0;

    for (const node of Array.from(tempDiv.childNodes)) {
        if (isEmptyParagraph(node)) {
            if (text) pendingBlankParagraphs += 1;
            continue;
        }

        const block = serializeBlock(node);
        if (!block) continue;

        if (text) {
            text += '\n\n';
            if (pendingBlankParagraphs > 1) {
                text += '\n'.repeat(pendingBlankParagraphs - 1);
            }
        }

        text += block;
        pendingBlankParagraphs = 0;
    }

    return text.trim();
}

export function hasIncidentCommentEditorText(html: string): boolean {
    if (!html) return false;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = normalizeIncidentCommentEditorHtml(html);

    if (tempDiv.textContent?.replace(/\u00a0/g, ' ').trim()) {
        return true;
    }

    return Boolean(tempDiv.querySelector('img, iframe, video, table, pre, blockquote'));
}
